/* /403 Forbidden — friendly, branded "Access denied" page.
 * No technical details / no admin-route names exposed. Two clear CTAs.
 */
import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldOff, Home, LogIn } from "lucide-react";
import BrandLogo from "@/components/BrandLogo";
import { useAuth } from "@/auth/AuthContext";

const Forbidden = () => {
  const navigate = useNavigate();
  const { logoutAdmin, logoutCustomer } = useAuth();

  useEffect(() => {
    const tag = document.createElement("meta");
    tag.name = "robots";
    tag.content = "noindex, nofollow";
    document.head.appendChild(tag);
    document.title = "Access denied · Mera Naksha";
    return () => {
      document.head.removeChild(tag);
    };
  }, []);

  const switchAccount = async () => {
    await Promise.all([logoutAdmin(), logoutCustomer()]);
    navigate("/customer/login", { replace: true });
  };

  return (
    <div
      className="min-h-screen bg-navy text-white grid place-items-center px-4 py-10 relative overflow-hidden"
      data-testid="forbidden-page"
    >
      <div className="absolute -top-32 -left-32 h-80 w-80 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -right-32 h-80 w-80 rounded-full bg-red-500/10 blur-3xl pointer-events-none" />
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold-dark via-gold to-gold-dark" />

      <div className="w-full max-w-lg relative">
        <div className="flex flex-col items-center gap-3 mb-7">
          <div className="bg-white rounded-xl px-3 py-2 shadow-md">
            <BrandLogo size="md" asLink={false} withTagline lang="en" testId="forbidden-brand-logo" />
          </div>
        </div>

        <div className="bg-white text-foreground rounded-3xl shadow-2xl border-2 border-red-300/40 overflow-hidden">
          <div className="bg-gradient-to-br from-red-50 to-white px-6 sm:px-8 pt-7 pb-6 text-center">
            <div className="mx-auto h-16 w-16 rounded-2xl bg-red-100 text-red-700 grid place-items-center border-2 border-red-300">
              <ShieldOff className="h-8 w-8" />
            </div>
            <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-navy">
              Access denied
            </h1>
            <p className="mt-2 text-[14px] text-muted-foreground leading-relaxed max-w-md mx-auto">
              You do not have permission to access this area. If you believe
              this is a mistake, please sign in with the correct account.
            </p>
          </div>

          <div className="px-6 sm:px-8 pb-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Link
              to="/"
              data-testid="forbidden-go-home"
              className="min-h-[48px] rounded-2xl bg-navy text-gold font-extrabold text-[14px] flex items-center justify-center gap-2 hover:bg-navy-deep transition-all"
            >
              <Home className="h-4 w-4" /> Go Home
            </Link>
            <button
              type="button"
              onClick={switchAccount}
              data-testid="forbidden-switch-account"
              className="min-h-[48px] rounded-2xl bg-gold text-navy font-extrabold text-[14px] flex items-center justify-center gap-2 hover:bg-gold-dark hover:text-white transition-all"
            >
              <LogIn className="h-4 w-4" /> Login with a different account
            </button>
          </div>

          <div className="px-6 sm:px-8 py-3 bg-navy/[0.04] border-t border-border text-[11px] text-muted-foreground text-center">
            Team Mera Naksha · Concept plans for Bharat
          </div>
        </div>
      </div>
    </div>
  );
};

export default Forbidden;
